import React, { useState, useContext } from "react";
import {
  HEADERTEXT_TYPES,
  WORKFLOW_SCHEDULER_TYPES,
} from "../functions/postActionTypes";
import { RouteComponentProps, useHistory } from "react-router-dom";
import {
  IonContent,
  IonPage,
  IonToolbar,
  IonGrid,
  IonRow,
  IonCol,
  IonButton,
  IonIcon,
  IonLabel,
  IonSegmentButton,
  useIonAlert,
  IonSegment,
  IonFab,
  IonFabButton,
  IonBadge,
  IonFooter,
  IonAvatar,
  IonModal,
  IonHeader,
  IonTitle,
  IonTextarea,
  IonAlert,
  useIonToast,
} from "@ionic/react";
import {
  chatboxOutline,
  megaphoneOutline,
  arrowUndo,
  closeOutline,
  send,
  sendOutline,
} from "ionicons/icons";
import "../theme/AppStyles.css";
import { ProfileContext } from "../context/ProfileContext";
import { SchedDetailsComponent } from "../components/ShiftDetailsSchedComponents/Details";
import { SchedWerkersComponent } from "../components/ShiftDetailsSchedComponents/Werkers";
import { WerkerSchedulerHeader } from "../components/WerkHeader";
import { useJobActions } from "../hooks/scheduler/useJobActions";
import { useJobData, SchedJobData } from "../hooks/scheduler/useJobData";
import { useLoadTimeoutGuard } from "../hooks/useLoadTimeoutGuard";
import { sendJobToSupport } from "../utils/chatUtils";
import { SUPPORT_MONKEY_ASSOCIATE } from "../constants/supportConstants";
import { LoadingScreen } from "../components/LoadingScreen";

interface ShiftDetailsProps
  extends RouteComponentProps<{
    id: string;
  }> {}

const SchedShiftDetails: React.FC<ShiftDetailsProps> = ({ match }) => {
  const { profile, usersID } = useContext(ProfileContext);
  const history = useHistory();
  const [present] = useIonAlert();
  const [presentSupportConfirm] = useIonAlert();
  const [presentToast] = useIonToast();
  const [showSupportDetails, setShowSupportDetails] = useState(false);
  const [supportNote, setSupportNote] = useState("");
  const [isSendingToSupport, setIsSendingToSupport] = useState(false);
  const [details, setDetails] = React.useState<string>("details");

  // Use shared job data fetching hook
  const { schedJobData } = useJobData({
    jobId: match.params.id,
    userId: usersID,
    isScheduler: true,
  });

  const hasLoadedSchedJob = Boolean(schedJobData && schedJobData.JobDocId);

  const navigateBackWithFallback = React.useCallback(() => {
    if (history.length > 1) {
      history.goBack();
      return;
    }

    history.replace("/SchedulerCalendar");
  }, [history]);

  const { isLoading: isLoadingSchedJob, hasTimedOut: hasTimedOutSchedJob } = useLoadTimeoutGuard({
    hasLoaded: hasLoadedSchedJob,
    resetKey: match.params.id,
    timeoutMs: 10000,
  });

  // Use default values if schedJobData is not yet loaded
  const thisSchedJob: SchedJobData = schedJobData || {
    JobDocId: "",
    ShiftIdentifier: "",
    SchedUid: "",
    DateDay: "",
    EndDay: "",
    StartDateTime: "",
    FinishDateTime: "",
    StartDateTimeRaw: "",
    FinishDateTimeRaw: "",
    Company: "",
    Location: "",
    LocationCoordinates: "",
    Pay: "",
    ShiftNotes: "",
    NumJobSlots: 0,
    JobStatusSched: "",
    JobStatusWerker: "",
    IsPaidJob: false,
    PaidWerker: false,
    PaidWerkers: false,
    JobCancelledByScheduler: false,
    CreatedAt: 0,
    UpdatedAt: 0,
    IsRead: false,
    IsNewMessage: false,
    scheduledCount: 0,
    invitedCount: 0,
    seriesId: null,
    seriesIndex: null,
    seriesTotal: null,
  };

  // Chat route
  const routeToChat = () => {
    history.push({
      pathname: `/Chat/${thisSchedJob.JobDocId}`,
      state: { msgCat: "JobMsg" },
    });
  };

  // Use shared job actions hook
  const {
    handleReminder,
    cancelJob,
    markComplete,
    unCompleteJob,
    markPaid,
    unCloseOut,
    unMarkPaid,
    deleteJob,
  } = useJobActions({
    jobId: match.params.id,
    userId: usersID,
    userFirstName: profile.FirstName,
    jobPay: thisSchedJob.Pay,
    jobCompany: thisSchedJob.Company,
    jobStartTime: thisSchedJob.StartDateTime,
    jobStartTimeRaw: thisSchedJob.StartDateTimeRaw,
    presentToast: presentToast,
    onConfirmReminder: (callback) => {
      present({
        header: "Re-alert everyone on this job?",
        buttons: [
          "Cancel",
          {
            text: "Ok",
            handler: () => {
              callback();
              return true;
            },
          },
        ],
      });
    },
    onConfirmDelete: (callback) => {
      present({
        header: "Delete Job?",
        message: "This will mark the job as DELETED for everyone.",
        buttons: ["Cancel", { text: "Delete", handler: () => { callback(); history.goBack(); } }],
      });
    },
    onScheduledWerkersFound: () => {
      present({
        header: "Can't delete this job",
        message: "Someone is still Scheduled on this job. Cancel or unassign them first.",
        buttons: ["OK"],
      });
    },
  });

  // Reminder - using hook
  const handleSchedShiftReminder = handleReminder;

  // Cancel job - using hook.
  // After the user confirms "Cancel Job?", chain a second prompt asking whether
  // they still want to pay the werker(s). "Yes" → keep current behavior (job
  // shows up in Payments → Unpaid). "No" → mark `paymentWaivedByScheduler` so the
  // job is excluded from the Unpaid list.
  const handleSchedCancel = () => {
    const werkerCount = thisSchedJob.scheduledCount || 0;
    const plural = werkerCount === 1 ? "Werker" : "Werkers";
    // Defer so the first alert finishes dismissing before we present the next.
    setTimeout(() => {
      present({
        header: `Do you still want to pay the ${plural} for this job?`,
        buttons: [
          { text: "No", handler: () => { cancelJob(true); } },
          { text: "Yes", handler: () => { cancelJob(false); } },
        ],
      });
    }, 350);
  };

  // Complete / undo complete - using hook
  const handleMarkComplete = markComplete;
  const handleUnCompleteJob = unCompleteJob;

  // Paid / un-paid - using hook
  const handleMarkPaid = markPaid;
  const _handleUnCloseOut = unCloseOut;

  const handleMarkJobPaid = () => {
    present({
      header: "Mark Job Paid?",
      buttons: ["Cancel", { text: "Ok", handler: handleMarkPaid }],
    });
  };

  const handleUndoJobPaid = () => {
    present({
      header: "Undo Mark Job Paid?",
      buttons: ["Cancel", { text: "OK", handler: unMarkPaid }],
    });
  };

  // Delete - using hook
  const handleDeleteJob = deleteJob;

  // ---------------------------
  // Route to clone/edit
  // ---------------------------
  const routeToClone = async () => {
    history.push({
      pathname: `/ScheduleJob/`,
      state: {
        msgCat: "JobClone",
        jobDocId: match.params.id,
      },
    });
  };

  const routeToEdit = async () => {
    history.push({
      pathname: `/ScheduleJob/`,
      state: {
        msgCat: "JobEdit",
        jobDocId: match.params.id,
      },
    });
  };

  const SchedShiftDetailsChatFab: React.FC<{ showAlert: boolean }> = ({ showAlert }) => {
    return (
      <IonFab
        slot="fixed"
        horizontal="end"
        vertical="bottom"
      >
        <IonFabButton
          color="secondwarning"
          style={{
            marginBottom: "40px",
            // "--background": "transparent",
            // "--background-activated": "rgba(255,255,255,0.08)",
            // "--background-hover": "rgba(255,255,255,0.06)",
            // "--color": "var(--ion-color-medium-contrast)",
            "--border-radius": "15px",
            "--box-shadow": "0 10px 26px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.10)",
          }}
          onClick={routeToChat}
        >
          <IonIcon
            icon={chatboxOutline}
            // color="medium"
          />
          {showAlert && (
            <IonBadge
              color="danger"
              style={{ position: "absolute", top: "7px", right: "4px" }}
            >
              {"1"}
            </IonBadge>
          )}
        </IonFabButton>
      </IonFab>
    );
  };

  const SchedShiftDetailsMessagesButton: React.FC = () => {
    return (
      <React.Fragment>
        <SchedShiftDetailsChatFab showAlert={thisSchedJob.IsNewMessage === true} />
      </React.Fragment>
    );
  };

  // ---------------------------
  // Segment selector (stays UP TOP)
  // ---------------------------
  const SchedShiftDetailsDetailsSelector: React.FC = () => {
    return (
      <IonToolbar
        style={{
          "--min-height": "40px",
          "--padding-top": "0px",
          "--padding-bottom": "0px",
        }}
      >
        <IonGrid>
          <IonRow>
            <IonCol
              size="12"
            >
              <IonSegment
                value={details}
                onIonChange={(e: any) => setDetails(e.detail.value)}
              >
                <IonSegmentButton
                  value="details"
                >
                  <IonLabel>
                    <h2>Details</h2>
                  </IonLabel>
                </IonSegmentButton>

                <IonSegmentButton
                  value="werkers"
                >
                  <IonLabel>
                    <h2>Werkers</h2>
                  </IonLabel>
                </IonSegmentButton>
              </IonSegment>
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonToolbar>
    );
  };

  // ---------------------------
  // Content by segment
  // ---------------------------
  const JobSummaryContent: React.FC = () => {
    if (details === "werkers") {
      return <SchedWerkersComponent jobDocId={match.params.id} seriesId={thisSchedJob.seriesId} />;
    }

    return (
      <SchedDetailsComponent
        jobDocId={match.params.id}
        schedJob={thisSchedJob}
        onEdit={routeToEdit}
        onClone={routeToClone}
        onDelete={() => void handleDeleteJob()}
        onMarkPaid={handleMarkJobPaid}
        onUndoPaid={handleUndoJobPaid}
      />
    );
  };

  // ---------------------------
  // Bottom actions
  // ---------------------------
  const handleSendInvite = () => {
    if (thisSchedJob.seriesId) {
      present({
        header: "Invite Werkers",
        message: "Invite for just this job or the whole series?",
        buttons: [
          { text: "Cancel", role: "cancel" },
          { text: "This Job", handler: () => { history.push(`/SelectWerkers/${match.params.id}`); } },
          { text: "Whole Series", handler: () => { history.push(`/SelectWerkersSeries/${thisSchedJob.seriesId}`); } },
        ],
      });
    } else {
      history.push(`/SelectWerkers/${match.params.id}`);
    }
  };

  const SchedAvailableJobButton: React.FC = () => {
    const hasInvited = (thisSchedJob.invitedCount ?? 0) > 0;
    return (
      <IonRow
        className="ion-padding-start ion-padding-end"
        style={{ marginBottom: 12 }}
      >
        <IonCol size="10">
          <IonButton
            onClick={hasInvited ? handleSchedShiftReminder : handleSendInvite}
            color="secondwarning"
            fill="solid"
            expand="block"
            style={{
              margin: 0,
              height: 42,
            }}
          >
            <IonIcon
              icon={hasInvited ? megaphoneOutline : sendOutline}
              slot="start"
            />
            {hasInvited ? "Re-alert" : "Send Invite"}
          </IonButton>
        </IonCol>

        <IonCol size="2">
          <IonButton
            onClick={() =>
              present({
                header: "Cancel Job?",
                buttons: ["No", { text: "Yes", handler: handleSchedCancel }],
              })
            }
            color="danger"
            fill="solid"
            expand="block"
            style={{
              margin: 0,
              height: 42,
              // "--border-radius": "14px",
            }}
          >
            <IonIcon
              icon={closeOutline}
              size="large"
              // slot="start"
            />
          </IonButton>
        </IonCol>
      </IonRow>
    );
  };

  const SchedScheduledJobButton: React.FC = () => {
    return (
      <IonRow
        className="ion-padding-start ion-padding-end"
        style={{ marginBottom: 12 }}
      >
        {/* <IonCol></IonCol> */}
        <IonCol size="10">
          <IonButton
            onClick={() =>
              present({
                header: "Mark as complete? All scheduled werkers will be marked complete as well.",
                buttons: ["Cancel", { text: "Ok", handler: handleMarkComplete }],
              })
            }
            color="secondwarning"
            style={{
              margin: 0,
              height: 42,
            }}
            fill="solid"
            expand="block"
          >
            Mark Complete
          </IonButton>
        </IonCol>
        <IonCol size="2">
          <IonButton
            onClick={() =>
              present({
                header: "Cancel Job?",
                buttons: ["No", { text: "Yes", handler: handleSchedCancel }],
              })
            }
            color="danger"
            style={{
              margin: 0,
              height: 42,
            }}
            fill="solid"
            expand="block"
          >
            <IonIcon
              icon={closeOutline}
            />
          </IonButton>
        </IonCol>
        <IonCol></IonCol>
      </IonRow>
    );
  };

  const SchedPastJobButton: React.FC = () => {
    return (
      <IonRow
        className="ion-padding-start ion-padding-end"
        style={{ marginBottom: 12 }}
      >
        {/* <IonCol></IonCol> */}
        <IonCol size="10">
          <IonButton
            // onClick={() =>
            //   present({
            //     header: "Mark as Paid?",
            //     buttons: ["Cancel", { text: "Ok", handler: handleMarkPaid }],
            //   })
            // }
            color="secondwarning"
            style={{
              margin: 0,
              height: 42,
            }}
            fill="solid"
            expand="block"
            disabled={thisSchedJob.JobCancelledByScheduler === true ? true : false}
          >
            Done
          </IonButton>
        </IonCol>
        <IonCol size="2">
          <IonButton
            onClick={() =>
              present({
                header: "Undo Completed Job?",
                buttons: ["Cancel", { text: "Ok", handler: handleUnCompleteJob }],
              })
            }
            color="medium"
            style={{
              margin: 0,
              height: 42,
            }}
            fill="solid"
            expand="block"
            disabled={thisSchedJob.JobCancelledByScheduler === true ? true : false}
          >
            <IonIcon
              icon={arrowUndo}
            />
          </IonButton>
        </IonCol>
        <IonCol></IonCol>
      </IonRow>
    );
  };

  const PaidJobButton: React.FC = () => {
    return (
      <IonRow
        className="ion-padding-start ion-padding-end"
        style={{ marginBottom: 12 }}
      >
        <IonCol size="12">
          <IonButton
            // onClick={() =>
            //   present({
            //     header: "Mark Un-Paid?",
            //     buttons: ["Cancel", { text: "Ok", handler: handleUnCloseOut }],
            //   })
            // }
            color="medium"
            style={{
              margin: 0,
              height: 42,
            }}
            fill="outline"
            expand="block"
            disabled={thisSchedJob.JobCancelledByScheduler === true}
          >
            {/* <IonIcon
              icon={arrowUndo}
              slot="start"
            /> */}
            Werkers Paid
          </IonButton>
        </IonCol>
      </IonRow>
    );
  };

  const JobSummaryActions: React.FC = () => {
    if (thisSchedJob.JobStatusSched === WORKFLOW_SCHEDULER_TYPES.Open) return <SchedAvailableJobButton />;
    if (thisSchedJob.JobStatusSched === WORKFLOW_SCHEDULER_TYPES.Filled) return <SchedScheduledJobButton />;
    if (thisSchedJob.JobStatusSched === WORKFLOW_SCHEDULER_TYPES.Past) return <SchedPastJobButton />;
    if (thisSchedJob.PaidWerkers === true) return <PaidJobButton />;
    return <SchedPastJobButton />;
  };

  return (
    <IonPage>
      {isLoadingSchedJob && (
        <IonContent
          className="schdbckgrnd"
          style={{ "--background": "#Ff7900" } as any}
        >
          <div
            style={{
              minHeight: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LoadingScreen LoadingText="" />
          </div>
        </IonContent>
      )}

      <IonAlert
        isOpen={hasTimedOutSchedJob}
        header="Job couldn't load. Please try again."
        backdropDismiss={false}
        buttons={[
          {
            text: "OK",
            handler: navigateBackWithFallback,
          },
        ]}
      />

      {!isLoadingSchedJob && !hasTimedOutSchedJob && (
        <>
          <WerkerSchedulerHeader
            title={HEADERTEXT_TYPES.ShiftDetails}
            subheader={<SchedShiftDetailsDetailsSelector />}
            endSlot={
              <IonButton
                fill="clear"
                style={{ "--padding-start": "6px", "--padding-end": "6px" } as any}
                onClick={() => {
                  presentSupportConfirm({
                    header: "Send job to support?",
                    message: "Our support team will review this job and get back to you via message.",
                    buttons: [
                      { text: "Cancel", role: "cancel" },
                      { text: "OK", handler: () => setShowSupportDetails(true) },
                    ],
                  });
                }}
              >
                <IonAvatar style={{ width: "28px", height: "28px" }}>
                  <img src={SUPPORT_MONKEY_ASSOCIATE.WerkerProfilePicURL} alt="Support" />
                </IonAvatar>
              </IonButton>
            }
          />

          {/* Support details modal */}
          <IonModal
            isOpen={showSupportDetails}
            onDidDismiss={() => { setShowSupportDetails(false); setSupportNote(""); }}
            initialBreakpoint={0.5}
            breakpoints={[0, 0.5, 0.75]}
          >
            <IonHeader>
              <IonToolbar>
                <IonTitle style={{ fontSize: "1rem" }}>Add Details (Optional)</IonTitle>
              </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding">
              <IonTextarea
                placeholder="Describe the issue (optional)..."
                autoGrow={true}
                rows={4}
                maxlength={500}
                spellCheck={true}
                autoCapitalize="sentences"
                value={supportNote}
                onIonChange={(e) => setSupportNote(e.detail.value || "")}
                style={{ marginBottom: "16px" }}
              />
              <IonButton
                expand="block"
                color="success"
                strong={true}
                disabled={isSendingToSupport}
                onClick={async () => {
                  setShowSupportDetails(false);
                  setIsSendingToSupport(true);
                  try {
                    await sendJobToSupport({
                      profile,
                      uniqueJobId: match.params.id,
                      schedUid: profile.UserId,
                      shiftIdentifier: thisSchedJob.ShiftIdentifier || "Job",
                      viewType: "scheduler",
                      userNote: supportNote,
                      startDate: thisSchedJob.DateDay || "",
                      location: thisSchedJob.Location || "",
                      docId: match.params.id || "",
                      seriesIndex: thisSchedJob.seriesIndex,
                      seriesTotal: thisSchedJob.seriesTotal,
                    });
                    setSupportNote("");
                    presentToast({ message: "Message sent to support \uD83D\uDC12", duration: 1500, position: "top", color: "medium" });
                  } catch (err) {
                    console.error("sendJobToSupport error:", err);
                    presentToast({ message: "Failed to send. Please try again.", duration: 2000, position: "top", color: "danger" });
                  } finally {
                    setIsSendingToSupport(false);
                  }
                }}
              >
                Send
              </IonButton>
              <IonButton
                expand="block"
                fill="clear"
                onClick={() => { setShowSupportDetails(false); setSupportNote(""); }}
              >
                Cancel
              </IonButton>
            </IonContent>
          </IonModal>

          <IonContent>
            <SchedShiftDetailsMessagesButton />
            <JobSummaryContent />
          </IonContent>

          <IonFooter>
            <IonToolbar
              style={{
                paddingBottom: "env(safe-area-inset-bottom)",
              }}
            >
              <JobSummaryActions />
            </IonToolbar>
          </IonFooter>
        </>
      )}
    </IonPage>
  );
};

export default SchedShiftDetails;
